import React, { useReducer, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { PLUGIN_REGISTRY, hydratePluginsFromServer, isPluginEnabled } from "./plugins/registry";
import { hydrateUserMatrixFromServer } from "./plugins/userMatrixStorage";
import { putAdminAppConfig } from "./plugins/appConfigApi";
import { readStoredAdminFlag } from "./authAdmin";
import { canViewerPermission, getViewerEmailFromStorage, isImpersonating } from "./plugins/userMatrixStorage";
import "./App.css";

/** Forces re-read from hydrated state after toggles (same-tab). */
function usePluginUiVersion() {
  const [v, bump] = useReducer((x) => x + 1, 0);
  React.useEffect(() => {
    const onChange = () => bump();
    window.addEventListener("pb-plugins-changed", onChange);
    return () => window.removeEventListener("pb-plugins-changed", onChange);
  }, [bump]);
  return v;
}

export default function PluginsPage() {
  usePluginUiVersion();
  const [toggleError, setToggleError] = useState("");
  const viewerEmail = getViewerEmailFromStorage();
  const canManagePlugins = canViewerPermission(viewerEmail, "canManagePlugins", true);

  if (!readStoredAdminFlag() || isImpersonating() || !canManagePlugins) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="plugins-page">
      {toggleError ? (
        <div className="auth-message" role="alert">
          <button type="button" className="link-button" style={{ float: "right" }} onClick={() => setToggleError("")}>
            Dismiss
          </button>
          {toggleError}
        </div>
      ) : null}
      <div className="plugins-page-header">
        <h1 className="plugins-page-title">Plugins</h1>
        <p className="plugins-page-lead">
          Enable or disable optional features for <strong>everyone</strong>. Plugin-specific settings are available from
          <Link to="/settings"> Settings</Link>.
        </p>
      </div>

      <ul className="plugins-list">
        {PLUGIN_REGISTRY.map((p) => {
          const on = isPluginEnabled(p.id);
          return (
            <li key={p.id} className="plugins-card">
              <div className="plugins-card-row">
                <div className="plugins-card-main">
                  <h2 className="plugins-card-name">{p.name}</h2>
                  <p className="plugins-card-desc">{p.description}</p>
                  <p className="plugins-card-id">
                    <code className="project-change-log-field">{p.id}</code>
                  </p>
                </div>
                <div className="plugins-card-actions">
                  <label className="plugins-toggle">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={async (e) => {
                        setToggleError("");
                        try {
                          const cfg = await putAdminAppConfig({
                            plugins: { [p.id]: e.target.checked },
                          });
                          hydratePluginsFromServer(cfg.plugins);
                          hydrateUserMatrixFromServer(cfg.userMatrix);
                        } catch (err) {
                          const status = err?.response?.status;
                          const apiErr = err?.response?.data?.error;
                          let msg =
                            typeof apiErr === "string"
                              ? apiErr
                              : err?.message || "Could not save plugin settings.";
                          if (status === 403) {
                            msg =
                              "Only database administrators can change plugins. Ask an admin or log in with an admin account.";
                          }
                          if (status === 503 || /missing|42P01/i.test(String(apiErr))) {
                            msg =
                              "Server configuration is incomplete (app settings table missing). Apply db/migration_project_board_app_settings.sql on Postgres and restart the API.";
                          }
                          setToggleError(msg);
                        }
                      }}
                    />
                    <span>{on ? "Enabled" : "Disabled"}</span>
                  </label>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <p className="plugins-page-footer">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
      </p>
    </div>
  );
}
